import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EventModel, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';
import { GridSubscriptionService } from '@frontend/sports/event-subscription/feature/grid-subscription';
import produce from 'immer';
import { clone } from 'lodash-es';
import { finalize } from 'rxjs';

import { EventCarouselSkeletonComponent } from '../../event-carousel/event-carousel-skeleton/event-carousel-skeleton.component';
import { EventCarouselComponent } from '../../event-carousel/event-carousel.component';
import { EventCarouselCustomItem, EventCarouselItem } from '../../event-carousel/event-carousel.model';
import { SheetViewCarouselDataService } from './sheet-view-carousel-data.service';
import { SheetViewCarouselStateService } from './sheet-view-carousel-state.service';

@Component({
    selector: 'ms-sheet-view-carousel',
    templateUrl: './sheet-view-carousel.component.html',
    styleUrl: './sheet-view-carousel.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [EventCarouselComponent, EventCarouselSkeletonComponent],
    providers: [SheetViewCarouselDataService],
})
export class SheetViewCarouselComponent implements OnInit, OnDestroy {
    readonly eventChange = output<{ event: EventModel; tileIndex: number }>();
    readonly periodChange = output<string>();
    readonly eventCustomTileChange = output<void>();

    sourceEvent = input.required<EventModel>();
    sourceOptionGroup = input.required<EventOptionGroup>();
    dynamicOfferCategory = input<string>();
    eventCarouselCustomItem = input<EventCarouselCustomItem>();
    eventCarouselItems = signal<EventCarouselItem[]>([]);
    selectedEventId = signal<string>('');
    showCarousel = signal<boolean>(false);
    isLoading = signal<boolean>(true);

    private gridSubscriptions: EventSubscription<EventModel>;

    private sheetViewCarouselDataService = inject(SheetViewCarouselDataService);
    private subscriptionsService = inject(GridSubscriptionService);
    private sheetViewCarouselStateService = inject(SheetViewCarouselStateService);
    private destroyRef = inject(DestroyRef);

    ngOnInit(): void {
        this.fetchData();
    }

    eventTileClicked({ eventCarouselItem, tileIndex }: { eventCarouselItem: EventCarouselItem; tileIndex: number }): void {
        const { event } = eventCarouselItem;
        if (event.id === this.selectedEventId()) {
            return;
        }

        this.selectedEventId.set(event.id);

        this.eventChange.emit({ event, tileIndex });
    }

    private fetchData(): void {
        this.sheetViewCarouselDataService
            .getFixtures(this.sourceEvent(), this.sourceOptionGroup(), this.dynamicOfferCategory(), this.eventCarouselCustomItem()?.state)
            .pipe(
                finalize(() => this.isLoading.set(false)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (eventCarouselItems: EventCarouselItem[] | undefined) => {
                    if (eventCarouselItems) {
                        this.selectedEventId.set(this.eventCarouselCustomItem()?.id || this.sourceEvent().id);
                        this.setEventList(eventCarouselItems);
                    }
                },
            });
    }

    private setEventList(eventCarouselItems: EventCarouselItem[]): void {
        this.eventCarouselItems.update((oldEventCarouselItems) => [...oldEventCarouselItems, ...eventCarouselItems]);
        const items = this.eventCarouselItems().length;

        if ((!!this.eventCarouselCustomItem()?.carouselItemTemplate && items > 0) || items > 1) {
            this.setGridSubscriptions(eventCarouselItems.map((item) => item.event));
            this.handleCarouselVisibility();
        }
    }

    private handleCarouselVisibility(): void {
        if (!this.showCarousel()) {
            this.showCarousel.set(true);
            this.sheetViewCarouselStateService.setShowEventCarousel(this.showCarousel());
        }
    }

    private updateEvents(updatedEvent: EventModel): void {
        const eventList = produce(this.eventCarouselItems(), (draft: EventCarouselItem[]) => this.findAndUpdateEvent(updatedEvent, draft));
        this.eventCarouselItems.set(eventList);
    }

    private findAndUpdateEvent(event: EventModel, draft: EventCarouselItem[]): void {
        const oldEventIndex = draft.findIndex((e) => e.event.id === event.id);
        if (oldEventIndex >= 0) {
            draft[oldEventIndex].event = clone(event);
            this.periodChange.emit(draft[oldEventIndex].event.scoreboard?.period?.name!);
        }
    }

    private setGridSubscriptions(eventList: EventModel[]): void {
        const eventSubs = this.subscriptionsService.subscribe(
            eventList.map((event) => ({
                event,
                callback: (updatedEvent) => this.updateEvents(updatedEvent),
            })),
        );

        if (this.gridSubscriptions) {
            this.gridSubscriptions.add(eventSubs);
        } else {
            this.gridSubscriptions = eventSubs;
        }
    }

    ngOnDestroy() {
        this.gridSubscriptions?.unsubscribe();
        this.sheetViewCarouselStateService.setShowEventCarousel(false);
    }

    eventCustomTileClicked(): void {
        const eventCarouselCustomItemId = this.eventCarouselCustomItem()?.id;
        if (eventCarouselCustomItemId) {
            if (eventCarouselCustomItemId === this.selectedEventId()) {
                return;
            }
            this.selectedEventId.set(eventCarouselCustomItemId);
            this.eventCustomTileChange.emit();
        }
    }
}
